/* istanbul ignore file */

import { compact, flow, get, isEmpty, map, noop, overEvery, size, times } from 'lodash/fp'
import { addFileProps, sendApplication } from 'e-submission/apps/actions'
import {
  canSubmitWithPayment,
  getAllAdditionalDocument,
  getFiles,
  getFileWithName,
  getPreviewMultipleDoc,
} from 'e-submission/apps/selectors'

import { createField, createSection, createSectionCreator } from 'e-submission/domain/data-model/form/utils'
import { DOCUMENT_TYPE } from 'e-submission/domain/data-model/constants'
import { previewDocumentSection } from 'e-submission/domain/data-model/submit'
import { ATPSection } from 'e-submission/domain/data-model/payment/ATPSection'
import {
  isATPSubmitted,
  isATPPaid,
  isRemoteSelling,
  isSelectedRemoteSelling,
} from 'e-submission/domain/data-model/form/selectors'

import { hasATP } from 'e-submission/domain/data-model/form/selectors'
import { isSectionValid } from 'e-submission/apps/selectors'

import { getToggles } from 'quick-quote/feature-toggles'

import { checkPaymentOptional } from '../../../form/submit/submitSection'

const mapWithKey = map.convert({ cap: false })

export default createSectionCreator((app, id) => {
  const isShowATP = hasATP(app)
  const isBrowseFile =
    (getToggles().ENABLE_INVESTMENT_PRODUCT_REMOTESELLING_WITHOUT_BANCA && isRemoteSelling(app)) ||
    isSelectedRemoteSelling(app) ||
    undefined
  // status submitted
  const isAppSubmitted = app.status === 'submitted'

  return [
    createField(
      'payment',
      compact([
        isShowATP && {
          id: 'ATP',
          c: 'MoreInfoCard',
          p: {
            subpage: 'atp',
            title: 'สมัครหักค่าเบี้ยประกันอัตโนมัติ ผ่านบัญชีธนาคาร',
            mergeState: (state, app) => {
              const sections = [ATPSection('ATP')(app)]
              const checked = overEvery([isATPPaid, isATPSubmitted, isSectionValid(state, sections)])(app)

              return {
                sections: sections,
                icon: checked ? 'check' : '',
                checked,
                paymentOptional: checkPaymentOptional(app),
              }
            },
          },
        },
      ]),
      {
        marginTopLess: true,
        pl: [0, 0, 2],
        pr: [0, 0, 2],
      }
    ),
    createField(
      'document',
      [
        {
          id: 'document',
          c: 'MoreInfoCard',
          p: {
            subpage: 'preview-document',
            title: 'เรียกดูเอกสารที่ลงนามเรียบร้อย',
            sections: [previewDocumentSection('preview-document')(app)],
          },
        },
        !isAppSubmitted && {
          id: 'insured.additionalDocument',
          c: 'MoreInfoCard',
          p: {
            subpage: 'additional-document',
            icon: 'illustrate',
            mergeState: (state) => {
              const documentType = get('insured.ADDITIONAL_DOCUMENT', DOCUMENT_TYPE)
              const documentTypeName = get('name', documentType)
              const documentTypeText = get('text', documentType)

              const additionalDocumentList = getAllAdditionalDocument(state)
              const totalAdditionalDoc = size(additionalDocumentList)
              const maxAdditionalDoc = 10

              const photoCardField = createField('insured.additionalDocument.documents', [
                {
                  id: 'insured.additionalDocument.documents',
                  c: 'PhotoCard',
                  required: true,
                  p: {
                    mergeState: (state, app) => {
                      const additionalDocumentList = getAllAdditionalDocument(state)
                      return {
                        value: mapWithKey(
                          (document, index) => ({
                            id: `insured.additionalDocument.documents[${index}]`,
                            name: get('name', document),
                            description: get('description', document),
                            checked: true,
                            editable: true,
                          }),
                          additionalDocumentList
                        ),
                      }
                    },
                  },
                },
              ])
              const addPhotoCardField = createField('insured.additionalDocument.add', [
                {
                  id: 'insured.additionalDocument.add',
                  c: 'PhotoCard',
                  required: true,
                  p: {
                    isBrowseFile,
                    mergeState: (state, app, { dispatch = noop } = {}) => {
                      const additionalDocumentList = getAllAdditionalDocument(state)
                      const totalAdditionalDoc = size(additionalDocumentList)
                      const description = flow(getFiles, get([documentTypeName, 'description']))(state)

                      return {
                        description,
                        disabled: isEmpty(description),
                        value:
                          totalAdditionalDoc < maxAdditionalDoc
                            ? [
                                {
                                  id: `insured.additionalDocument.documents[${totalAdditionalDoc}]`,
                                  name: documentTypeName,
                                  checked: false,
                                  editable: true,
                                  placeholder: 'กรุณากรอกข้อมูล',
                                  onChange: (value) => {
                                    dispatch(
                                      addFileProps(documentTypeName, {
                                        description: value,
                                      })
                                    )
                                  },
                                },
                              ]
                            : [],
                      }
                    },
                  },
                },
              ])
              const placeHolderFields = times((index) =>
                createField(`insured.additionalDocument.placeHolder[${index}]`, [
                  {
                    id: `insured.additionalDocument.placeHolder[${index}]`,
                    c: 'PlaceHolderCard',
                  },
                ])
              )(maxAdditionalDoc - totalAdditionalDoc - 1)

              return {
                title: `${documentTypeText} (ถ้ามี)`,
                sections: [
                  createSection(
                    compact([
                      size(additionalDocumentList) > 0 && photoCardField,
                      addPhotoCardField,
                      ...placeHolderFields,
                    ])
                  ),
                ],
              }
            },
          },
        },
      ],
      {
        marginTopLess: true,
        pl: [0, 0, 2],
        pr: [0, 0, 2],
      }
    ),
    createField('insured.submitApplication', [
      {
        id: 'insured.submitApplication',
        c: 'Button',
        p: {
          mergeState: (state, app, { dispatch, history }) => {
            let disabled = !canSubmitWithPayment(state)
            return {
              disabled,
              onClick: () => {
                dispatch(sendApplication(history))
              },
            }
          },
          children: 'ส่งใบคำขอ',
          block: true,
          icon: 'wifi',
          style: {
            fontSize: '1.175rem',
            marginTop: '40px',
          },
        },
      },
    ]),
    createField(
      'insured.submitApplicationDiscliamer',
      [
        {
          id: 'insured.submitApplicationDiscliamer',
          c: 'MutedText',
          p: {
            children: 'กรุณาต่อ Internet เพื่อส่งใบคำขอ',
          },
        },
      ],
      {
        style: {
          marginTop: '-1rem',
        },
      }
    ),
  ]
})
